import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Session, ConsoleLog, SessionMetadata } from '../types';

// UI utilities
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Date utilities
export function formatDate(date: string | number | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return format(dateObj, 'MMM dd, yyyy HH:mm:ss');
}

export function formatRelativeTime(date: string | number | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
  return formatDistanceToNow(dateObj, { addSuffix: true });
}

export function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

// Session utilities
export function getSessionStats(session: Session) {
  const consoleLogs = session.consoleLogs || [];
  const networkRequests = session.networkRequests || [];
  
  const errorLogs = consoleLogs.filter(log => log.level === 'error');
  const warningLogs = consoleLogs.filter(log => log.level === 'warn');
  const failedRequests = networkRequests.filter(req => 
    req.status && (req.status >= 400 || req.error)
  );

  return {
    hasErrors: errorLogs.length > 0,
    hasWarnings: warningLogs.length > 0,
    hasConsoleLogs: consoleLogs.length > 0,
    hasNetworkRequests: networkRequests.length > 0,
    hasFailedRequests: failedRequests.length > 0,
    errorCount: errorLogs.length,
    warningCount: warningLogs.length,
    consoleLogCount: consoleLogs.length,
    networkRequestCount: networkRequests.length,
    failedRequestCount: failedRequests.length,
  };
}

export function getSessionStatus(session: Session): 'success' | 'warning' | 'error' {
  const stats = getSessionStats(session);
  
  if (stats.hasErrors || stats.hasFailedRequests) {
    return 'error';
  } else if (stats.hasWarnings) {
    return 'warning';
  } else {
    return 'success';
  }
}

// Console log utilities
export function formatConsoleMessage(log: ConsoleLog): string {
  if (log.args.length === 0) {
    return log.message;
  }
  
  try {
    return log.args.map(arg => {
      if (typeof arg === 'string') {
        return arg;
      } else if (typeof arg === 'object') {
        return JSON.stringify(arg, null, 2);
      } else {
        return String(arg);
      }
    }).join(' ');
  } catch {
    return log.message;
  }
}

export function getConsoleLogIcon(level: ConsoleLog['level']) {
  switch (level) {
    case 'error':
      return '🔴';
    case 'warn':
      return '🟡';
    case 'info':
      return '🔵';
    case 'debug':
      return '🟢';
    default:
      return '⚪';
  }
}

// Network request utilities
export function getRequestStatusColor(status?: number): string {
  if (!status) return 'text-gray-500';
  
  if (status >= 200 && status < 300) return 'text-green-600';
  if (status >= 300 && status < 400) return 'text-primary-600';
  if (status >= 400 && status < 500) return 'text-amber-600';
  if (status >= 500) return 'text-red-600';
  
  return 'text-gray-500';
}

export function getRequestMethodColor(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'bg-green-100 text-green-800';
    case 'POST':
      return 'bg-primary-100 text-primary-800';
    case 'PUT':
      return 'bg-amber-100 text-amber-800';
    case 'DELETE':
      return 'bg-red-100 text-red-800';
    case 'PATCH':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

// URL utilities
export function getDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

export function truncateUrl(url: string, maxLength: number = 50): string {
  if (url.length <= maxLength) return url;
  
  const domain = getDomain(url);
  const path = new URL(url).pathname;
  
  if (domain.length + path.length <= maxLength) {
    return url;
  }
  
  return `${domain}${path.substring(0, maxLength - domain.length - 3)}...`;
}

// Browser utilities
export function getBrowserInfo(userAgent: string): { browser: string; version: string } {
  // Simple browser detection
  if (userAgent.includes('Chrome')) {
    return { browser: 'Chrome', version: 'Chrome' };
  } else if (userAgent.includes('Firefox')) {
    return { browser: 'Firefox', version: 'Firefox' };
  } else if (userAgent.includes('Safari')) {
    return { browser: 'Safari', version: 'Safari' };
  } else if (userAgent.includes('Edge')) {
    return { browser: 'Edge', version: 'Edge' };
  } else {
    return { browser: 'Unknown', version: 'Unknown' };
  }
}

// Pagination utilities
export function getPageNumbers(currentPage: number, totalPages: number, maxVisible: number = 5) {
  const pages: (number | string)[] = [];
  
  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    const halfVisible = Math.floor(maxVisible / 2);
    
    if (currentPage <= halfVisible + 1) {
      for (let i = 1; i <= maxVisible - 1; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    } else if (currentPage >= totalPages - halfVisible) {
      pages.push(1);
      pages.push('...');
      for (let i = totalPages - maxVisible + 2; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      pages.push('...');
      for (let i = currentPage - halfVisible; i <= currentPage + halfVisible; i++) {
        pages.push(i);
      }
      pages.push('...');
      pages.push(totalPages);
    }
  }
  
  return pages;
}

// Storage utilities
export function setLocalStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
}

export function getLocalStorage<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Failed to read from localStorage:', error);
    return defaultValue;
  }
} 

/**
 * Get country flag emoji from country code
 */
export function getCountryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '';
  
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt(0));
  
  return String.fromCodePoint(...codePoints);
}

/**
 * Format location data for display
 */
export function formatLocation(geolocation?: SessionMetadata['geolocation']): string {
  if (!geolocation) return 'Unknown';
  
  const parts = [];
  
  if (geolocation.city) {
    parts.push(geolocation.city);
  }
  
  if (geolocation.regionName && geolocation.regionName !== geolocation.city) {
    parts.push(geolocation.regionName);
  }
  
  if (geolocation.country) {
    parts.push(geolocation.country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Unknown';
}

/**
 * Get location display with flag
 */
export function getLocationDisplay(geolocation?: SessionMetadata['geolocation']): { 
  text: string; 
  flag: string; 
  hasLocation: boolean 
} {
  if (!geolocation) {
    return { text: 'Unknown', flag: '', hasLocation: false };
  }
  
  const text = formatLocation(geolocation);
  const flag = geolocation.countryCode ? getCountryFlag(geolocation.countryCode) : '';
  
  return { text, flag, hasLocation: true };
} 